import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";

const FAQ_CATEGORIES = [
  {
    category: "Getting Started",
    faqs: [
      { q: "What is Satta Matka?", a: "Satta Matka is a popular number-based game of chance in India. Players bet on numbers (Single Digit 0–9, Jodi 00–99, or Patti 3-digit combinations). Results are declared at specific times daily for each market. Winners receive payouts based on the game type and win rates." },
      { q: "Is Play Online Satta Matka safe to use?", a: "Yes. We use OTP-based login (no stored passwords), secured wallet transactions and verified payment processing. Your personal data is never sold. Your account is protected by mobile OTP every login. We also follow responsible gaming guidelines — play is strictly for users 18+ only." },
      { q: "How do I register on Play Online Satta Matka?", a: "Tap Register on the homepage. Enter your 10-digit mobile number. Enter the OTP sent to your phone. Your account is created instantly — no email, no documents required." },
      { q: "Is this platform available in my state?", a: "It is the user's responsibility to verify whether playing skill/chance-based games is permitted in their state or location. Play Online Satta Matka is an entertainment platform. If gaming is restricted in your area, do not register." },
      { q: "Can I play on my mobile phone?", a: "Yes. The website is fully mobile-optimized. You can also download the Android app from the link on the homepage. Works on all Android devices and mobile browsers on iPhone." },
    ],
  },
  {
    category: "Deposits & Withdrawals",
    faqs: [
      { q: "How do I add money to my wallet?", a: "Go to Wallet → Add Money. Enter the amount you want to deposit. Scan the UPI QR code or use the UPI ID shown. Pay with PhonePe, Google Pay, Paytm or any UPI app. Upload the payment screenshot. Your balance is credited after verification, usually within minutes during business hours." },
      { q: "What is the minimum deposit amount?", a: "The minimum deposit amount is shown on the Add Money screen inside your wallet. It may vary depending on current platform settings. Check the app for the latest minimum." },
      { q: "How long does it take for my deposit to show?", a: "Deposits are credited after manual verification of your payment screenshot. During active hours (9 AM – 9 PM), this is usually done within minutes. If delayed beyond 30 minutes, contact support with your UPI transaction ID." },
      { q: "How do I withdraw my winnings?", a: "Go to Wallet → Withdraw. Enter the amount you want to withdraw. Make sure you have added your bank account or UPI details in your Profile first. Your withdrawal request is processed the same day during business hours." },
      { q: "How long does withdrawal take?", a: "Withdrawals are typically processed the same business day. During high volume periods, processing may take up to 24 hours. Make sure your bank account or UPI details are correct — incorrect details may delay or reject the transfer." },
      { q: "What payment methods are accepted?", a: "We accept all major UPI payment methods including PhonePe, Google Pay, Paytm, BHIM UPI and any bank's UPI app. Bank transfer (IMPS/NEFT) may also be available depending on the current settings." },
    ],
  },
  {
    category: "Game Types & Rules",
    faqs: [
      { q: "What game types are available?", a: "Play Online Satta Matka offers 8 game types: Single Digit (9×), Jodi (90×), Red Bracket, Single Patti – SP (150×), Double Patti – DP (270×), Triple Patti – TP (800×), Half Sangam (1500×) and Full Sangam (11,250×)." },
      { q: "What is a Jodi in Satta Matka?", a: "Jodi is a 2-digit result from 00 to 99 formed by combining the Open Digit and Close Digit of a market. If you bet ₹10 on Jodi 45 and the result is 45, you win ₹900 (90× your bet)." },
      { q: "What is the difference between Single Patti and Double Patti?", a: "Single Patti (SP) is a 3-digit number where all three digits are different (e.g. 123). Win rate: 150×. Double Patti (DP) has two same digits (e.g. 112). Win rate: 270×. Triple Patti (TP) has all three same (e.g. 111). Win rate: 800×." },
      { q: "Can I cancel a bid after placing it?", a: "No. Once a valid bid is placed before market closing, it cannot be cancelled. Please confirm your number, amount and game type carefully before submitting. Admin-level reversal is only done in case of verified technical errors." },
      { q: "What happens if I place a bid after market close?", a: "Bids placed after the market close time are not accepted. The system prevents late submissions during open hours. Always check market status (Open/Closed) before placing a bid." },
      { q: "What are the market timings?", a: "Market timings vary. Popular examples: Kalyan (Mon–Sat, Open 3:45 PM / Close 5:45 PM), Milan Night (Open 9:00 PM / Close 10:00 PM), Main Bazar (Open 9:30 PM / Close 12:00 AM). Check live market cards on the home screen for exact current timings." },
    ],
  },
  {
    category: "Results & Charts",
    faqs: [
      { q: "Where can I see live Matka results?", a: "Live results are shown on the home screen market cards. They are updated automatically after results are declared. You can also go to Charts → select a market → view historical results." },
      { q: "What is a Jodi Chart?", a: "A Jodi Chart shows the 2-digit Jodi result for each day in a calendar grid. It helps players see historical patterns across weeks and months. Available under Charts → select market → Jodi Chart." },
      { q: "What is a Panel Chart?", a: "A Panel Chart (Pana Chart) shows the Open Pana, Jodi and Close Pana for every date. More detailed than Jodi chart — shows full 3-digit results. Available under Charts → select market → Pana Chart." },
      { q: "How quickly are results updated?", a: "Results are updated as soon as they are declared by the market. Our home screen refreshes automatically every 30 seconds. You can also tap the Refresh button on the home screen for instant update." },
    ],
  },
  {
    category: "Account & Security",
    faqs: [
      { q: "How do I log in to my account?", a: "Enter your registered mobile number on the Login screen. Tap Send OTP. Enter the 6-digit OTP received on your phone. You are logged in instantly — no password needed." },
      { q: "What should I do if I don't receive the OTP?", a: "Check your network signal. Try again after 60 seconds using the Resend OTP button. Make sure the number you entered is your registered mobile number. If still no OTP, contact support." },
      { q: "How do I update my bank account or UPI details?", a: "Go to Profile → Bank Accounts. Add or update your bank account number and IFSC code or UPI ID. Verification may be required before you can use new details for withdrawal." },
      { q: "What if I see an unexpected transaction in my wallet?", a: "Contact support immediately at support@playonlinesattamatka.com with your registered mobile number, transaction date, and amount. Do not share your OTP with anyone." },
    ],
  },
];

function FaqPage() {
  const navigate    = useNavigate();
  const [open, setOpen] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [vw, setVw] = useState(() => window.innerWidth);
  useEffect(() => {
    const h = () => setVw(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    const prev    = document.title;
    const descEl  = document.querySelector("meta[name='description']");
    const prevDesc = descEl?.getAttribute("content") || "";
    document.title = "FAQ – Frequently Asked Questions | Play Online Satta Matka";
    descEl?.setAttribute("content", "Frequently asked questions about Play Online Satta Matka — how to register, deposit, withdraw, place bets, read charts, and play responsibly.");
    return () => {
      document.title = prev;
      if (descEl) descEl.setAttribute("content", prevDesc);
    };
  }, []);

  const allFaqs = FAQ_CATEGORIES.flatMap((c) => c.faqs.map((f) => ({ ...f, category: c.category })));
  const visibleFaqs = activeCat
    ? allFaqs.filter((f) => f.category === activeCat)
    : allFaqs;

  return (
    <main style={pg}>
      <header style={topbar}>
        <button type="button" style={iconBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
        </button>
        <span style={brand}>
          <span style={{ color: "#f97316" }}>PLAY ONLINE</span>
          <span style={{ color: "#16a34a" }}> SATTA MATKA</span>
        </span>
        <button type="button" style={playBtn} onClick={() => navigate("/login")}>Play Now</button>
      </header>

      <section style={heroSection}>
        <p style={eyebrow}>Help Center</p>
        <h1 style={heroH1}>Frequently Asked Questions</h1>
        <p style={heroPara}>Everything you need to know about Play Online Satta Matka — accounts, deposits, game types, results and more.</p>
        <div style={{ ...statRow, gap: vw <= 480 ? 24 : 36 }}>
          <div style={stat}><b style={{ fontSize: 24, fontWeight: 900 }}>{allFaqs.length}+</b><span>Questions answered</span></div>
          <div style={stat}><b style={{ fontSize: 24, fontWeight: 900 }}>{FAQ_CATEGORIES.length}</b><span>Topic categories</span></div>
        </div>
      </section>

      <div style={{ ...body, padding: vw <= 768 ? "24px 16px 48px" : "32px 24px 60px" }}>
        {/* Category filter */}
        <div style={catRow}>
          <button style={activeCat === null ? catBtnActive : catBtn} onClick={() => setActiveCat(null)}>All Topics</button>
          {FAQ_CATEGORIES.map((c) => (
            <button key={c.category} style={activeCat === c.category ? catBtnActive : catBtn} onClick={() => setActiveCat(c.category)}>
              {c.category}
            </button>
          ))}
        </div>

        {/* FAQ accordion */}
        <div style={faqList}>
          {visibleFaqs.map((faq, i) => {
            const key = `${faq.category}-${i}`;
            const isOpen = open === key;
            return (
              <article key={key} style={faqItem}>
                <button
                  type="button"
                  style={faqQ}
                  onClick={() => setOpen(isOpen ? null : key)}
                  aria-expanded={isOpen}
                >
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <span style={faqCatTag}>{faq.category}</span>
                    <span style={faqQText}>{faq.q}</span>
                  </div>
                  {isOpen ? <ChevronUp size={18} color="#f97316" /> : <ChevronDown size={18} color="#6b7280" />}
                </button>
                {isOpen && <p style={faqA}>{faq.a}</p>}
              </article>
            );
          })}
        </div>

        {/* Still need help */}
        <div style={helpBox}>
          <h2 style={helpTitle}>Still have a question?</h2>
          <p style={helpPara}>Our support team is here to help. Contact us by email or WhatsApp and we'll get back to you quickly.</p>
          <div style={helpBtns}>
            <button style={helpPrimary} onClick={() => navigate("/contact-us")}>Contact Support →</button>
            <button style={helpSecondary} onClick={() => navigate("/blog")}>Read Our Blog →</button>
          </div>
        </div>
      </div>

      <footer style={ftr}>
        <p style={ftrNote}>© 2026 Play Online Satta Matka | 18+ Only | Play Responsibly</p>
        <div style={ftrLinks}>
          {[["Home", "/"], ["Blog", "/blog"], ["Charts", "/charts"], ["How to Play", "/how-to-play"], ["About Us", "/about-us"], ["Contact Us", "/contact-us"]].map(([l, p]) => (
            <button key={p} style={ftrLnk} onClick={() => navigate(p)}>{l}</button>
          ))}
        </div>
      </footer>
    </main>
  );
}

const pg          = { minHeight: "100vh", background: "#f9fafb", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827" };
const topbar      = { display: "flex", alignItems: "center", gap: 14, padding: "12px 20px", background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10 };
const iconBtn     = { width: 36, height: 36, border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 };
const brand       = { flex: 1, fontWeight: 900, fontSize: 17, fontFamily: "'Arial Black', sans-serif" };
const playBtn     = { height: 36, padding: "0 16px", border: 0, borderRadius: 8, background: "#f97316", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" };

const heroSection = { background: "#312e81", color: "#fff", padding: "44px 24px 36px", textAlign: "center" };
const eyebrow     = { margin: "0 0 8px", fontSize: 12, fontWeight: 800, color: "#fbbf24", textTransform: "uppercase", letterSpacing: "0.1em" };
const heroH1      = { margin: "0 0 12px", fontSize: "clamp(24px,4vw,36px)", fontWeight: 900 };
const heroPara    = { margin: "0 0 24px", fontSize: 16, color: "rgba(255,255,255,.8)", maxWidth: 600, marginLeft: "auto", marginRight: "auto" };
const statRow     = { display: "flex", justifyContent: "center", gap: 36 };
const stat        = { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, color: "rgba(255,255,255,.8)", fontSize: 13 };

const body        = { maxWidth: 860, margin: "0 auto", padding: "32px 24px 60px" };
const catRow      = { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 };
const catBtn      = { height: 36, padding: "0 16px", border: "1.5px solid #e5e7eb", borderRadius: 999, background: "#fff", color: "#374151", fontWeight: 700, fontSize: 13, cursor: "pointer" };
const catBtnActive = { ...catBtn, background: "#312e81", borderColor: "#312e81", color: "#fff" };

const faqList     = { display: "flex", flexDirection: "column", gap: 10, marginBottom: 36 };
const faqItem     = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" };
const faqQ        = { width: "100%", display: "flex", alignItems: "flex-start", gap: 14, padding: "16px 18px", border: 0, background: "transparent", cursor: "pointer", textAlign: "left" };
const faqCatTag   = { display: "block", fontSize: 10, fontWeight: 800, color: "#2563eb", textTransform: "uppercase", marginBottom: 5 };
const faqQText    = { display: "block", fontSize: 15, fontWeight: 700, color: "#111827", lineHeight: 1.4 };
const faqA        = { margin: 0, padding: "0 18px 16px 18px", fontSize: 14, color: "#374151", lineHeight: 1.75, borderTop: "1px solid #f3f4f6" };

const helpBox     = { background: "#312e81", borderRadius: 16, padding: "32px 28px", color: "#fff", textAlign: "center" };
const helpTitle   = { margin: "0 0 10px", fontSize: 22, fontWeight: 900 };
const helpPara    = { margin: "0 0 22px", fontSize: 15, color: "rgba(255,255,255,.8)" };
const helpBtns    = { display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" };
const helpPrimary = { height: 44, padding: "0 24px", border: 0, borderRadius: 10, background: "#f97316", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" };
const helpSecondary = { height: 44, padding: "0 22px", border: "2px solid rgba(255,255,255,.5)", borderRadius: 10, background: "transparent", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" };

const ftr         = { background: "#111827", color: "#fff", padding: "24px", textAlign: "center" };
const ftrNote     = { fontSize: 12, color: "#9ca3af", marginBottom: 12 };
const ftrLinks    = { display: "flex", flexWrap: "wrap", gap: "6px 16px", justifyContent: "center" };
const ftrLnk      = { border: 0, background: "transparent", color: "#d1d5db", fontSize: 13, fontWeight: 600, cursor: "pointer" };

export default FaqPage;
